import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styles from '../PlantDesignDetails.module.scss';
import { FormGroup, Input } from '@snsw/react-component-library';
import { isEmpty, isNumeric, validateExt } from 'shared/utils/Validation';
import { PlantDesignDetailsPage } from 'plantDesign/constants/plantDesignDetailsPage';
import { mapRefDataPickList } from 'shared/utils/Helpers';
import { PlantDesignRefData } from 'plantDesign/constants/PlantDesignRefData';
import { RadioOrDropdownSelector } from 'shared/components/selector/RadioOrDropdownSelector';

export const PassengerRopewayDesignDetails = React.forwardRef((props, ref) => {
  const {
    carrierType: savedCarrierType = '',
    otherCarrierType: savedOtherCarrierType = '',
    maxNumberOfCarriers: savedMaxNumberOfCarriers = '',
    numberOfCompressionTensionTowers: savedNumberOfCompressionTensionTowers = '',
    maxNumberOfPersons: savedMaxNumberOfPersons = '',
    maxHeightOfCarrierAboveSurface: savedMaxHeightOfCarrierAboveSurface = '',
    maxNumberOfPersonPerCarrier: savedMaxNumberOfPersonPerCarrier = '',
    numberOfTowers: savedNumberOfTowers = '',
    travelDistanceSingleDirection: savedTravelDistanceSingleDirection = '',
    numberOfFacilities: savedNumberOfFacilities = '',
  } = props.value || {};

  const [carrierType, setCarrierType] = useState(savedCarrierType);
  const [otherCarrierType, setOtherCarrierType] = useState(savedOtherCarrierType);
  const [maxNumberOfCarriers, setMaxNumberOfCarriers] = useState(savedMaxNumberOfCarriers);
  const [numberOfCompressionTensionTowers, setNumberOfCompressionTensionTowers] = useState(
    savedNumberOfCompressionTensionTowers,
  );
  const [maxNumberOfPersons, setMaxNumberOfPersons] = useState(savedMaxNumberOfPersons);
  const [maxHeightOfCarrierAboveSurface, setMaxHeightOfCarrierAboveSurface] = useState(
    savedMaxHeightOfCarrierAboveSurface,
  );
  const [maxNumberOfPersonPerCarrier, setMaxNumberOfPersonPerCarrier] = useState(savedMaxNumberOfPersonPerCarrier);
  const [numberOfTowers, setNumberOfTowers] = useState(savedNumberOfTowers);
  const [travelDistanceSingleDirection, setTravelDistanceSingleDirection] = useState(
    savedTravelDistanceSingleDirection,
  );
  const [numberOfFacilities, setNumberOfFacilities] = useState(savedNumberOfFacilities);

  const [showErrors, setShowErrors] = useState(false);
  const [errors, setErrors] = useState({});
  const [errorSummary, setErrorSummary] = useState([]);

  const REF_CONSTANTS = PlantDesignRefData.passengerRopeway;
  const PAGE_CONSTANTS = PlantDesignDetailsPage.passengerRopeway;

  const carrierTypeOptionsAll = mapRefDataPickList(props.refData[REF_CONSTANTS.carrierTypeOptions]);
  const [carrierTypeOptions, setCarrierTypeOptions] = useState([]);

  useEffect(() => {
    const passengerRopeTypeOptions = mapRefDataPickList(props.refData[REF_CONSTANTS.passengerRopeTypeOptions]);

    let typeDescription = passengerRopeTypeOptions.find((element) => element.value === props.subType);
    if (typeDescription) {
      setCarrierTypeOptions(carrierTypeOptionsAll.filter((e) => e.condition === typeDescription.condition));
    }
  }, [props.subType]);

  const validators = [
    {
      fieldName: 'carrierType',
      fieldValue: carrierType,
      isValid: (v) => !isEmpty(v),
      errorMessage: () => 'Please select an option',
    },
    {
      fieldName: 'carrierType_other',
      fieldValue: otherCarrierType,
      isValid: (v) => !isEmpty(v) || carrierType !== 'Other (please describe)',
      errorMessage: () => 'Please enter details',
    },
    {
      fieldName: 'maxNumberOfCarriers',
      fieldValue: maxNumberOfCarriers,
      isValid: isNumeric,
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
    {
      fieldName: 'numberOfCompressionTensionTowers',
      fieldValue: numberOfCompressionTensionTowers,
      isValid: isNumeric,
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
    {
      fieldName: 'maxNumberOfPersons',
      fieldValue: maxNumberOfPersons,
      isValid: (v) => isEmpty(v) || isNumeric(v),
      errorMessage: () => 'Please enter only numeric value',
    },
    {
      fieldName: 'maxHeightOfCarrierAboveSurface',
      fieldValue: maxHeightOfCarrierAboveSurface,
      isValid: (v) => isEmpty(v) || isNumeric(v),
      errorMessage: () => 'Please enter only numeric value',
    },
    {
      fieldName: 'maxNumberOfPersonPerCarrier',
      fieldValue: maxNumberOfPersonPerCarrier,
      isValid: isNumeric,
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
    {
      fieldName: 'numberOfTowers',
      fieldValue: numberOfTowers,
      isValid: isNumeric,
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
    {
      fieldName: 'travelDistanceSingleDirection',
      fieldValue: travelDistanceSingleDirection,
      isValid: (v) => isEmpty(v) || isNumeric(v),
      errorMessage: () => 'Please enter only numeric value',
    },
    {
      fieldName: 'numberOfFacilities',
      fieldValue: numberOfFacilities,
      isValid: isNumeric,
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
  ];

  if (ref) {
    ref.current = {
      validate: () => {
        const isValid = validateExt(validators, setErrors, setErrorSummary);
        setShowErrors(!isValid);
        return isValid;
      },
    };
  }

  useEffect(() => {
    const details = {
      carrierType,
      otherCarrierType,
      maxNumberOfCarriers,
      numberOfCompressionTensionTowers,
      maxNumberOfPersons,
      maxHeightOfCarrierAboveSurface,
      maxNumberOfPersonPerCarrier,
      numberOfTowers,
      travelDistanceSingleDirection,
      numberOfFacilities,
    };

    // TODO: remove maxHeightOfCarrierAboveSurface if(subType !== 'Above-Surface')

    props.onChange(details);

    if (showErrors) validateExt(validators, setErrors, setErrorSummary);
  }, [
    carrierType,
    otherCarrierType,
    maxNumberOfCarriers,
    numberOfCompressionTensionTowers,
    maxNumberOfPersons,
    maxHeightOfCarrierAboveSurface,
    maxNumberOfPersonPerCarrier,
    numberOfTowers,
    travelDistanceSingleDirection,
    numberOfFacilities,
  ]);

  useEffect(() => {
    props.onErrorChange(errorSummary);
  }, [errorSummary]);

  return (
    <>
      {props.subType && (
        <>
          <RadioOrDropdownSelector
            id="carrierType"
            name="carrierType"
            options={carrierTypeOptions}
            title={PAGE_CONSTANTS.carrierType.label}
            errorMessage={errors?.carrierType?.errorMessage}
            hasError={showErrors && errors?.carrierType?.invalid}
            onChange={(v) => {
              setCarrierType(v);
              setOtherCarrierType('');
            }}
            value={carrierType}
            showExtraInput={carrierType === 'Other (please describe)'}
            onChangeExtraInput={setOtherCarrierType}
            extraInputValue={otherCarrierType}
            extraInputErrorMessage={errors?.carrierType_other?.errorMessage}
            extraInputHasError={showErrors && errors?.carrierType_other?.invalid}
            extraInputTitle={'Other please describe'}
            margin={{ top: 'xl' }}
            maxOptionsLengthForRadio={2}
            extraInputType="textarea"
          />

          <FormGroup
            id="maxNumberOfCarriers"
            label={PAGE_CONSTANTS.maxNumberOfCarriers.label}
            isRequired
            errorMessage={errors?.maxNumberOfCarriers?.errorMessage}
            hasError={showErrors && errors?.maxNumberOfCarriers?.invalid}
            className={styles.inputAlignWithInputSuffix}
            margin={{ top: 'md' }}
          >
            <Input
              onChange={(e) => setMaxNumberOfCarriers(e.target.value)}
              value={maxNumberOfCarriers}
              inputWidth="lg"
            />
          </FormGroup>

          <FormGroup
            id="numberOfCompressionTensionTowers"
            label={PAGE_CONSTANTS.numberOfCompressionTensionTowers.label}
            isRequired
            errorMessage={errors?.numberOfCompressionTensionTowers?.errorMessage}
            hasError={showErrors && errors?.numberOfCompressionTensionTowers?.invalid}
            className={styles.inputAlignWithInputSuffix}
            margin={{ top: 'md' }}
          >
            <Input
              onChange={(e) => setNumberOfCompressionTensionTowers(e.target.value)}
              value={numberOfCompressionTensionTowers}
              inputWidth="md"
            />
          </FormGroup>

          <FormGroup
            id="maxNumberOfPersons"
            label={PAGE_CONSTANTS.maxNumberOfPersons.label}
            isRequired
            errorMessage={errors?.maxNumberOfPersons?.errorMessage}
            hasError={showErrors && errors?.maxNumberOfPersons?.invalid}
            className={styles.inputAlignWithInputSuffix}
            margin={{ top: 'md' }}
          >
            <Input onChange={(e) => setMaxNumberOfPersons(e.target.value)} value={maxNumberOfPersons} inputWidth="md" />
          </FormGroup>

          {props.subType === 'Above-Surface' && (
            <FormGroup
              id="maxHeightOfCarrierAboveSurface"
              label={PAGE_CONSTANTS.maxHeightOfCarrierAboveSurface.label}
              isRequired
              errorMessage={errors?.maxHeightOfCarrierAboveSurface?.errorMessage}
              hasError={showErrors && errors?.maxHeightOfCarrierAboveSurface?.invalid}
              className={styles.inputWithSuffix}
              margin={{ top: 'md' }}
            >
              <Input
                suffix={PAGE_CONSTANTS.maxHeightOfCarrierAboveSurface.suffix}
                onChange={(e) => setMaxHeightOfCarrierAboveSurface(e.target.value)}
                value={maxHeightOfCarrierAboveSurface}
                inputWidth="md"
              />
            </FormGroup>
          )}

          <FormGroup
            id="maxNumberOfPersonPerCarrier"
            label={PAGE_CONSTANTS.maxNumberOfPersonPerCarrier.label}
            isRequired
            errorMessage={errors?.maxNumberOfPersonPerCarrier?.errorMessage}
            hasError={showErrors && errors?.maxNumberOfPersonPerCarrier?.invalid}
            className={styles.inputAlignWithInputSuffix}
            margin={{ top: 'md' }}
          >
            <Input
              onChange={(e) => setMaxNumberOfPersonPerCarrier(e.target.value)}
              value={maxNumberOfPersonPerCarrier}
              inputWidth="md"
            />
          </FormGroup>

          <FormGroup
            id="numberOfTowers"
            label={PAGE_CONSTANTS.numberOfTowers.label}
            isRequired
            errorMessage={errors?.numberOfTowers?.errorMessage}
            hasError={showErrors && errors?.numberOfTowers?.invalid}
            className={styles.inputAlignWithInputSuffix}
            margin={{ top: 'md' }}
          >
            <Input onChange={(e) => setNumberOfTowers(e.target.value)} value={numberOfTowers} inputWidth="md" />
          </FormGroup>

          <FormGroup
            id="travelDistanceSingleDirection"
            label={PAGE_CONSTANTS.travelDistanceSingleDirection.label}
            isRequired
            errorMessage={errors?.travelDistanceSingleDirection?.errorMessage}
            hasError={showErrors && errors?.travelDistanceSingleDirection?.invalid}
            className={styles.inputWithSuffix}
            margin={{ top: 'md' }}
          >
            <Input
              suffix={PAGE_CONSTANTS.travelDistanceSingleDirection.suffix}
              onChange={(e) => setTravelDistanceSingleDirection(e.target.value)}
              value={travelDistanceSingleDirection}
              inputWidth="md"
            />
          </FormGroup>

          <FormGroup
            id="numberOfFacilities"
            label={PAGE_CONSTANTS.numberOfFacilities.label}
            isRequired
            errorMessage={errors?.numberOfFacilities?.errorMessage}
            hasError={showErrors && errors?.numberOfFacilities?.invalid}
            className={styles.inputAlignWithInputSuffix}
            margin={{ top: 'md' }}
          >
            <Input onChange={(e) => setNumberOfFacilities(e.target.value)} value={numberOfFacilities} inputWidth="md" />
          </FormGroup>
        </>
      )}
    </>
  );
});

PassengerRopewayDesignDetails.displayName = 'PassengerRopewayDesignDetails';

PassengerRopewayDesignDetails.propTypes = {
  value: PropTypes.object,
  subType: PropTypes.string,
  refData: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  onErrorChange: PropTypes.func.isRequired,
};
